<div class="library-page">
  <div class="library-page__inner">
    <div class="library-page__header">
      <div class="library-page__title-row">
        <h1 class="library-page__title">{{ 'tenders.library.title' | translate }}</h1>
        <ex-pill variant="neutral" class="library-page__count" [class.library-page__count--hidden]="state.totalCount() === 0">
          {{
            (state.totalCount() === 1 ? 'tenders.library.doc-count-one' : 'tenders.library.doc-count-many')
              | translate: { count: state.totalCount() || 0 }
          }}
        </ex-pill>
      </div>

      @if (
        state.documents().length > 0 ||
        state.query() ||
        state.activeSubtypeIds().length ||
        state.hasActiveImport() ||
        state.totalCount() > 0
      ) {
        <div class="library-page__toolbar">
          <ex-button type="primary" icon="plus" size="large" (click)="openImportPanel()">
            {{ 'tenders.library.import' | translate }}
          </ex-button>
          <div class="library-page__filters">
            <ex-filter-text
              class="library-page__search"
              [placeholder]="'tenders.library.search-placeholder' | translate"
              [text]="state.query()"
              [debounceMs]="250"
              variant="light"
              size="compact"
              (textEnter)="onSearch($event)"
            />
            <ex-sort-dropdown
              [items]="sortItems"
              [defaultValue]="sortDefault"
              [useShortLabel]="true"
              compact
              [shadowBorder]="false"
              (sort)="onSort($event)"
            />
          </div>
        </div>

        <app-document-type-filter-bar
          class="library-page__chips"
          [subtypes]="state.filterSubtypes()"
          [activeIds]="state.activeSubtypeIds()"
          [counts]="state.subtypeCounts()"
          (toggle)="state.toggleSubtype($event)"
          (clear)="state.clearSubtypes()"
        />
      }
    </div>

    @if (state.showImportProgress() || state.importDone() || state.hasPendingDuplicates()) {
      <div class="library-page__banners">
        @if (state.showImportProgress()) {
          <div class="library-import-card">
            <button
              type="button"
              class="library-import-card__toggle"
              (click)="toggleImportProgress()"
              [attr.aria-expanded]="importProgressExpanded"
            >
              <i class="ph ph-folder library-import-card__icon" aria-hidden="true"></i>
              <span class="library-import-card__title">
                {{
                  (state.uploadTotal() === 1 ? 'tenders.library.import-progress-title-one' : 'tenders.library.import-progress-title-many')
                    | translate: { count: state.uploadTotal() }
                }}
              </span>
              <span class="library-import-card__spacer"></span>
              @if (state.progressPhase() === 'extracting') {
                <ex-pill variant="info" [showSpinner]="true">
                  {{ 'tenders.responses.documents.extraction-in-progress' | translate }}
                </ex-pill>
              } @else if (state.progressPhase() === 'uploading') {
                <ex-pill variant="info" [showSpinner]="true">
                  {{
                    'tenders.responses.documents.upload-loading'
                      | translate: { completed: state.uploadCompleted(), total: state.uploadTotal() }
                  }}
                </ex-pill>
              } @else if (state.progressPhase() === 'analyzing') {
                <ex-pill variant="warning" [showSpinner]="true">
                  {{
                    'tenders.responses.documents.categorization-loading'
                      | translate: { completed: state.analysisCompleted(), total: state.analysisTotal() }
                  }}
                </ex-pill>
              }
              <i
                class="ph ph-caret-down library-import-card__caret"
                [class.library-import-card__caret--expanded]="importProgressExpanded"
                aria-hidden="true"
              ></i>
            </button>

            @if (state.progressPhase() === 'uploading' || state.progressPhase() === 'analyzing') {
              <div
                class="library-import-card__batch-bar"
                role="progressbar"
                [attr.aria-valuemin]="0"
                [attr.aria-valuemax]="100"
                [attr.aria-valuenow]="state.batchProgressPercent()"
              >
                <div
                  class="library-import-card__batch-bar-fill"
                  [class.library-import-card__batch-bar-fill--warning]="state.progressPhase() === 'analyzing'"
                  [style.width.%]="state.batchProgressPercent()"
                ></div>
              </div>
            }

            <div class="library-import-card__expandable" [class.library-import-card__expandable--collapsed]="!importProgressExpanded">
              <div class="library-import-card__list">
                @for (upload of state.uploads(); track upload.clientKey) {
                  <div class="library-import-card__row">
                    <app-document-identity
                      class="library-import-card__identity"
                      [name]="upload.name"
                      [fileSize]="upload.size"
                      [subtypeName]="uploadSubtypeName(upload)"
                      [processingStep]="uploadProcessingStep(upload)"
                      [uploadProgress]="upload.progress ?? null"
                      [isArchive]="!!upload.isArchive"
                      [showUncategorized]="!!upload.uncategorized"
                    />
                  </div>
                }
              </div>
              <app-document-import-zone
                class="library-import-card__add"
                variant="compact"
                addLabel="tenders.responses.documents.picker.import-more"
                (filesSelected)="onFilesSelected($event)"
                (filesRejected)="onZoneRejected($event)"
              />
            </div>
          </div>
        } @else if (state.importDone()) {
          <div class="library-banner library-banner--success">
            <div class="library-banner__icon"><i class="ph ph-check" aria-hidden="true"></i></div>
            <div class="library-banner__body">
              <p class="library-banner__title">
                @if (state.pendingRefreshCount() > 0) {
                  {{
                    (state.pendingRefreshCount() === 1 ? 'tenders.library.import-added-one' : 'tenders.library.import-added-many')
                      | translate: { count: state.pendingRefreshCount() }
                  }}
                  @if (state.replacedCount() > 0) {
                    ·
                    {{
                      (state.replacedCount() === 1 ? 'tenders.library.import-replaced-one' : 'tenders.library.import-replaced-many')
                        | translate: { count: state.replacedCount() }
                    }}
                  }
                } @else {
                  {{ 'tenders.library.import-done' | translate }}
                }
              </p>
              <p class="library-banner__subtitle">
                @if (state.pendingRefreshCount() > 0) {
                  {{ 'tenders.library.import-refresh-hint' | translate }}
                } @else {
                  {{ 'tenders.library.import-replaced-hint' | translate }}
                }
              </p>
            </div>
            <ex-button type="primary" size="large" icon="arrow-clockwise" (click)="state.refresh()">
              {{ 'tenders.library.refresh' | translate }}
            </ex-button>
          </div>
          @if (state.rejected().length) {
            <app-ignored-files-notice [files]="state.rejected()" />
          }
        }

        @if (state.hasPendingDuplicates()) {
          <app-duplicate-resolver surface="library" [items]="state.duplicates()" (resolve)="onDuplicateResolve($event)" />
        }
      </div>
    }

    @if (!state.showImportProgress() && !state.hasPendingDuplicates() && state.hasUncategorized()) {
      <div class="library-page__banners">
        <div class="library-uncat-card">
          <div class="library-uncat-card__header">
            <div class="library-uncat-card__icon" aria-hidden="true"><i class="ph ph-warning"></i></div>
            <div class="library-uncat-card__head-text">
              <div class="library-uncat-card__title">
                @if (state.uncategorizedDocuments().length === 1) {
                  {{ 'tenders.responses.documents.uncategorized-banner-one-lead' | translate }}
                } @else {
                  {{
                    'tenders.responses.documents.uncategorized-banner-many-lead'
                      | translate: { count: state.uncategorizedDocuments().length }
                  }}
                }
              </div>
            </div>
            @if (state.showUncategorized()) {
              <button type="button" class="library-uncat-card__link" (click)="toggleUncategorized()">
                {{ 'tenders.responses.documents.dedup.reduce' | translate }}
              </button>
            } @else {
              <ex-button type="primary" size="large" (click)="toggleUncategorized()">
                {{ 'tenders.library.categorize-cta' | translate }}
              </ex-button>
            }
          </div>
          @if (state.showUncategorized()) {
            <div class="library-uncat-card__list">
              @for (doc of state.uncategorizedDocuments(); track doc.id) {
                <div class="library-uncat-card__row">
                  <app-document-identity
                    class="library-uncat-card__identity"
                    metaVariant="library"
                    [name]="doc.name"
                    [fileSize]="doc.fileSize"
                    [showUncategorized]="true"
                    [processingStep]="DocumentProcessingStepEnum.UNCATEGORIZED"
                    [uploaderName]="doc.uploader?.name"
                    [updatedAt]="doc.updatedAt"
                    [usedCount]="doc.projects?.length ?? 0"
                  />
                  <app-category-selector
                    [options]="state.filterSubtypes()"
                    [value]="null"
                    [uncategorized]="true"
                    (optionSelected)="onCategorySelected(doc, $event)"
                    (customSelected)="onCategoryCustom(doc, $event)"
                  />
                </div>
              }
            </div>
          }
        </div>
      </div>
    }

    @if (state.loading()) {
      <div class="library-page__list-card">
        <div class="library-page__loading">
          @for (_ of [1, 2, 3, 4, 5, 6]; track $index) {
            <div class="library-skeleton-row" aria-hidden="true">
              <div class="library-skeleton-row__icon"></div>
              <div class="library-skeleton-row__body">
                <div class="library-skeleton-row__name"></div>
                <div class="library-skeleton-row__meta"></div>
              </div>
              <div class="library-skeleton-row__category"></div>
              <div class="library-skeleton-row__menu"></div>
            </div>
          }
        </div>
      </div>
    } @else if (!state.documents().length && !state.query() && !state.activeSubtypeIds().length && !state.hasActiveImport()) {
      <div class="library-empty">
        <app-document-import-zone
          variant="dropzone"
          [mainTitle]="'tenders.library.empty-title' | translate"
          [subtitle]="'tenders.library.empty-subtitle' | translate"
          [browseLabel]="'tenders.library.import-cta'"
          [hints]="emptyHints"
          [footnote]="emptyFootnote"
          (filesSelected)="onFilesSelected($event)"
          (filesRejected)="onZoneRejected($event)"
        />
      </div>
    } @else {
      <div class="library-page__list-card" infiniteScroll (endScroll)="state.loadMore()">
        @if (!state.documents().length) {
          <p class="library-page__no-results">{{ 'tenders.library.no-results' | translate }}</p>
        } @else {
          @for (doc of state.documents(); track doc.id) {
            <div class="library-row">
              <app-document-identity
                class="library-row__identity"
                metaVariant="library"
                [name]="doc.name"
                [subtypeName]="isUncategorized(doc) ? null : state.subtypeOf(doc)?.name"
                [showUncategorized]="isUncategorized(doc)"
                [processingStep]="rowProcessingStep(doc)"
                [uploaderName]="doc.uploader?.name"
                [updatedAt]="doc.updatedAt"
                [usedCount]="doc.projects?.length ?? 0"
              />
              <app-category-selector
                class="library-row__category"
                [options]="state.filterSubtypes()"
                [value]="categoryValue(doc)"
                [uncategorized]="isUncategorized(doc)"
                (optionSelected)="onCategorySelected(doc, $event)"
                (customSelected)="onCategoryCustom(doc, $event)"
              />
              <app-document-row-menu [showView]="false" (download)="state.downloadDocument(doc)" (delete)="onDelete(doc)" />
            </div>
          }
          @if (state.loadingMore()) {
            <div class="library-page__loading-more">
              @for (_ of [1, 2, 3]; track $index) {
                <div class="library-skeleton-row" aria-hidden="true">
                  <div class="library-skeleton-row__icon"></div>
                  <div class="library-skeleton-row__body">
                    <div class="library-skeleton-row__name"></div>
                    <div class="library-skeleton-row__meta"></div>
                  </div>
                  <div class="library-skeleton-row__category"></div>
                  <div class="library-skeleton-row__menu"></div>
                </div>
              }
            </div>
          }
        }
      </div>
    }
  </div>

  @if (importPanelOpen) {
    <div class="library-import-overlay" (click)="closeImportPanel()">
      <div class="library-import-panel" (click)="$event.stopPropagation()">
        <div class="library-import-panel__header">
          <h2>{{ 'tenders.library.import' | translate }}</h2>
          <button
            type="button"
            class="library-import-panel__close"
            (click)="closeImportPanel()"
            [attr.aria-label]="'common.close' | translate"
          >
            <i class="ph ph-x" aria-hidden="true"></i>
          </button>
        </div>
        <app-document-import-zone
          variant="dropzone"
          [mainTitle]="'tenders.library.import-drop-title' | translate"
          [subtitle]="'tenders.library.import-drop-subtitle' | translate"
          (filesSelected)="onFilesSelected($event)"
          (filesRejected)="onZoneRejected($event)"
        />
      </div>
    </div>
  }
</div>
